import type { PatientResponse, WeekDateRangeResponse, YearPlanResponse } from '../api/generated';
import type { EconomicsPatientNode, EconomicsTreeModel } from '../types/economicsTypes';
import type { EconomicsExportLabels, EconomicsExportRow } from '../types/economicsExportTypes';
import {
  ECONOMICS_EXPORT_COLUMN_WIDTHS,
  ECONOMICS_EXPORT_DATE_FORMAT,
  ECONOMICS_EXPORT_FILE_PREFIX,
  ECONOMICS_EXPORT_INTEGER_FORMAT,
  ECONOMICS_EXPORT_NUMBER_FORMAT,
  ECONOMICS_EXPORT_SHEET_NAME_MAX_LENGTH
} from '../constants/economicsConstants';
import { downloadExcel } from '../utils/excelUtils';
import type { ExcelColumn } from '../utils/excelUtils';
import { isoStringToDate } from '../utils/dateUtils';

const ISO_DATE_LENGTH = 10;

export interface EconomicsExportInput {
  yearPlan: YearPlanResponse;
  tree: EconomicsTreeModel;
  patientsById: Record<string, PatientResponse>;
}

type WeekRangeByNumber = Map<number, WeekDateRangeResponse>;

function indexWeeks(weeks: WeekDateRangeResponse[]): WeekRangeByNumber {
  return new Map(weeks.map((week) => [week.weekNumber, week]));
}

function patientRows(
  patient: EconomicsPatientNode,
  planName: string,
  cprNumber: string,
  weeksByNumber: WeekRangeByNumber,
  labels: EconomicsExportLabels
): EconomicsExportRow[] {
  const rows: EconomicsExportRow[] = patient.groups
    .filter((node) => node.completed)
    .map((node) => {
      const range = weeksByNumber.get(node.weekNumber);
      return {
        planName,
        patientName: patient.patientName,
        cprNumber,
        moduleName: node.groupName,
        weekNumber: node.weekNumber,
        startDate: range?.startDate ?? null,
        endDate: range?.endDate ?? null,
        amount: node.effectiveAmount,
        isOverridden: node.isOverridden,
        note: node.isOverridden ? node.note : null,
        completedAt: node.completedAt
      };
    });

  if (patient.completionClaim?.claimed) {
    rows.push({
      planName,
      patientName: patient.patientName,
      cprNumber,
      moduleName: labels.completionClaim,
      weekNumber: null,
      startDate: null,
      endDate: null,
      amount: patient.completionClaim.amount,
      isOverridden: false,
      note: null,
      completedAt: patient.completionClaim.claimedAt
    });
  }
  return rows;
}

// Rows without a week (completion claims) sort after every week.
const NO_WEEK = Number.MAX_SAFE_INTEGER;

function weekOrder(row: EconomicsExportRow): number {
  return row.weekNumber ?? NO_WEEK;
}

// Rows arrive sorted by week, so a group's first row carries its first completed week. Equal
// first weeks fall back to patient name and then CPR, so the order never depends on plan order.
function compareCprGroups(a: EconomicsExportRow[], b: EconomicsExportRow[]): number {
  return (
    weekOrder(a[0]) - weekOrder(b[0]) ||
    a[0].patientName.localeCompare(b[0].patientName, 'da') ||
    a[0].cprNumber.localeCompare(b[0].cprNumber)
  );
}

// Flattens the (already period-filtered) economics tree into billable rows: one per completed
// group seat plus one per claimed completion reimbursement. Uncompleted seats are not billed and
// therefore not exported. Rows are grouped per CPR number across plans, the groups ordered by the
// first week the patient completed something, and each group's rows by week with completion
// claims last (rows sharing a week keep the tree's plan order). A patient without a CPR number is
// grouped on their own.
export function buildExportRows(input: EconomicsExportInput, labels: EconomicsExportLabels): EconomicsExportRow[] {
  const weeksByNumber = indexWeeks(input.yearPlan.weeks);
  const rowsByCpr = new Map<string, EconomicsExportRow[]>();

  for (const planNode of input.tree.plans) {
    for (const patient of planNode.patients) {
      const cprNumber = input.patientsById[patient.patientId]?.cprNumber ?? '';
      const rows = patientRows(patient, planNode.planName, cprNumber, weeksByNumber, labels);
      if (rows.length === 0) continue;
      const groupKey = cprNumber || patient.patientId;
      const group = rowsByCpr.get(groupKey);
      if (group) group.push(...rows);
      else rowsByCpr.set(groupKey, rows);
    }
  }
  const groups = [...rowsByCpr.values()];
  for (const rows of groups) {
    rows.sort((a, b) => weekOrder(a) - weekOrder(b));
  }
  groups.sort(compareCprGroups);
  return groups.flat();
}

// Keeps letters and digits, collapsing everything else into single dashes. Splitting on a
// single-character class runs in linear time, unlike an anchored trim regex (Sonar S5852).
function sanitizeFileNamePart(value: string): string {
  return value
    .split(/[^\p{L}\p{N}]/u)
    .filter((part) => part !== '')
    .join('-');
}

export function buildExportFileName(
  locationName: string | null,
  year: number,
  fromWeek: number,
  toWeek: number
): string {
  const weekPart = fromWeek === toWeek ? `uge${fromWeek}` : `uge${fromWeek}-${toWeek}`;
  const parts = [ECONOMICS_EXPORT_FILE_PREFIX, locationName ? sanitizeFileNamePart(locationName) : null, year, weekPart];
  return `${parts.filter((part) => part !== null && part !== '').join('_')}.xlsx`;
}

// Accepts both plain dates (week ranges) and date-times (completedAt); only the calendar day matters.
function toDate(iso: string | null): Date | null {
  return iso ? (isoStringToDate(iso.slice(0, ISO_DATE_LENGTH)) ?? null) : null;
}

export function buildExportColumns(labels: EconomicsExportLabels): ExcelColumn<EconomicsExportRow>[] {
  const c = labels.columns;
  const w = ECONOMICS_EXPORT_COLUMN_WIDTHS;
  return [
    { header: c.planName, kind: 'text', width: w.planName, value: (r) => r.planName },
    { header: c.patientName, kind: 'text', width: w.patientName, value: (r) => r.patientName },
    { header: c.cprNumber, kind: 'text', width: w.cprNumber, value: (r) => r.cprNumber, total: 'countDistinct' },
    { header: c.moduleName, kind: 'text', width: w.moduleName, value: (r) => r.moduleName },
    { header: c.weekNumber, kind: 'integer', width: w.weekNumber, value: (r) => r.weekNumber },
    { header: c.startDate, kind: 'date', width: w.startDate, value: (r) => toDate(r.startDate) },
    { header: c.endDate, kind: 'date', width: w.endDate, value: (r) => toDate(r.endDate) },
    { header: c.amount, kind: 'number', width: w.amount, value: (r) => r.amount, total: 'sum' },
    { header: c.isOverridden, kind: 'text', width: w.isOverridden, value: (r) => (r.isOverridden ? labels.yes : labels.no) },
    { header: c.note, kind: 'text', width: w.note, value: (r) => r.note ?? '' },
    { header: c.completedAt, kind: 'date', width: w.completedAt, value: (r) => toDate(r.completedAt) }
  ];
}

export async function exportEconomicsToExcel(
  input: EconomicsExportInput,
  fileName: string,
  labels: EconomicsExportLabels
): Promise<void> {
  const rows = buildExportRows(input, labels);
  await downloadExcel(buildExportColumns(labels), rows, {
    fileName,
    sheetName: labels.sheetName.slice(0, ECONOMICS_EXPORT_SHEET_NAME_MAX_LENGTH),
    numberFormat: ECONOMICS_EXPORT_NUMBER_FORMAT,
    integerFormat: ECONOMICS_EXPORT_INTEGER_FORMAT,
    dateFormat: ECONOMICS_EXPORT_DATE_FORMAT,
    totalLabel: labels.total
  });
}
